import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import AdminMenu from './common/menus/AdminMenu';
import { useAuth } from '../contexts/AuthContext';
import materiaService from '../services/MateriaService';
import usuarioService from '../services/UsuarioService';
import aulaService from '../services/AulaService';
import { ADMIN_DASHBOARD_MENU_ITEMS, navigateAdminMenuItem } from '../utils/adminMenu';
import LoadingSpinner from './common/LoadingSpinner';

const AdminEditAula = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { aulaId } = useParams();
  const { user, logout } = useAuth();
  const aulaState = location.state?.aula || {};
  const stateColegioId = location.state?.idColegio || null;

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => stateColegioId || ColegioService.getActiveColegioId(), [stateColegioId]);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';

  const [materias, setMaterias] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nombre: aulaState.nombre || aulaState.Nombre || '',
    descripcion: aulaState.descripcion || aulaState.Descripcion || '',
    nicknameUrl: aulaState.nicknameUrl || aulaState.NicknameUrl || '',
    codigoSuscripcion: aulaState.codigoSuscripcion || aulaState.CodigoSuscripcion || '',
    aulaPublica: !!(aulaState.aulaPublica ?? aulaState.AulaPublica),
    idMateria: aulaState.idMateria || aulaState.IdMateria || '',
    idProfesor: aulaState.idProfesor || aulaState.IdProfesor || '',
    cicloLectivo: aulaState.cicloLectivo || aulaState.CicloLectivo || currentYear,
    idColegio: aulaState.idColegio || aulaState.IdColegio || activeColegioId || ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    if (!aulaId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [aulaId, navigate]);

  useEffect(() => {
    const fetchAula = async () => {
      if (aulaState && Object.keys(aulaState).length > 0) return;
      if (!aulaId) return;
      try {
        const aula = await aulaService.getAulaById(aulaId);
        if (aula) {
          setForm({
            nombre: aula.nombre || aula.Nombre || '',
            descripcion: aula.descripcion || aula.Descripcion || '',
            nicknameUrl: aula.nicknameUrl || aula.NicknameUrl || '',
            codigoSuscripcion: aula.codigoSuscripcion || aula.CodigoSuscripcion || '',
            aulaPublica: !!(aula.aulaPublica ?? aula.AulaPublica),
            idMateria: aula.idMateria || aula.IdMateria || '',
            idProfesor: aula.idProfesor || aula.IdProfesor || '',
            cicloLectivo: aula.cicloLectivo || aula.CicloLectivo || currentYear,
            idColegio: aula.idColegio || aula.IdColegio || activeColegioId || ''
          });
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el aula.');
        setMessageType('error');
      }
    };
    fetchAula();
  }, [aulaId, aulaState, activeColegioId, currentYear]);

  useEffect(() => {
    const fetchData = async () => {
      if (!activeColegioId) return;
      setIsLoadingData(true);
      try {
        const materiasRes = await materiaService.getMateriasActivas({ idColegio: activeColegioId });
        setMaterias(Array.isArray(materiasRes?.items) ? materiasRes.items : Array.isArray(materiasRes) ? materiasRes : []);
        const profesoresRes = await usuarioService.getProfesoresPorColegio(activeColegioId);
        setProfesores(Array.isArray(profesoresRes) ? profesoresRes : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los datos necesarios.');
        setMessageType('error');
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();

    if (activeColegioId && !form.idColegio) {
      setForm((prev) => ({ ...prev, idColegio: activeColegioId }));
    }
  }, [activeColegioId, form.idColegio]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del aula.');
      setMessageType('error');
      return;
    }
    if (!form.idMateria) {
      setMessage('Selecciona la materia.');
      setMessageType('error');
      return;
    }
    if (!form.nicknameUrl.trim()) {
      setMessage('Completa el nickname del aula.');
      setMessageType('error');
      return;
    }
    if (!form.idProfesor) {
      setMessage('Selecciona el profesor.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Guardando cambios...');
    setMessageType('info');
    try {
      await aulaService.updateAula(aulaId, {
        nombre: form.nombre,
        descripcion: form.descripcion,
        nicknameUrl: form.nicknameUrl,
        codigoSuscripcion: '',
        aulaPublica: form.aulaPublica,
        idMateria: form.idMateria,
        idProfesor: form.idProfesor,
        cicloLectivo: Number(form.cicloLectivo) || currentYear,
        idColegio: form.idColegio
      });
      setMessage('Aula actualizada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'aulas' } }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos actualizar el aula.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <AdminMenu
          activeId="aulas"
          user={user}
          featureFlags={ColegioService.getFeatureFlags()}
          baseItems={ADMIN_DASHBOARD_MENU_ITEMS}
          onNavigate={(item) => navigateAdminMenuItem(navigate, item)}
        />
        <div className="sidebar-footer">
          <ActiveColegioBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar aula</h1>
            <p className="page-subtitle">Actualiza el aula del colegio {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <ActiveColegioBadge />
            <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-door-open" />
                <span>Datos del aula</span>
              </div>
            </div>
            <div className="card-content">
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                  />
                </div>
                <div className="form-field">
                  <label>Nickname URL</label>
                  <input
                    type="text"
                    value={form.nicknameUrl}
                    onChange={(e) => handleChange('nicknameUrl', e.target.value)}
                    placeholder="Ej: aula-bio-2026"
                  />
                </div>
                <div className="form-field checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={form.aulaPublica}
                      onChange={(e) => handleChange('aulaPublica', e.target.checked)}
                    />
                    Aula pública
                  </label>
                </div>
                <div className="form-field">
                  <label>Materia</label>
                  <select
                    value={form.idMateria}
                    onChange={(e) => handleChange('idMateria', e.target.value)}
                    disabled={isLoadingData}
                  >
                    <option value="">Selecciona una materia</option>
                    {materias.map((m) => (
                      <option key={m.id || m.Id} value={m.id || m.Id}>
                        {m.nombre || m.Nombre}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Profesor</label>
                  <select
                    value={form.idProfesor}
                    onChange={(e) => handleChange('idProfesor', e.target.value)}
                    disabled={isLoadingData}
                  >
                    <option value="">Selecciona un profesor</option>
                    {profesores.map((p) => (
                      <option key={p.id || p.Id} value={p.id || p.Id}>
                        {p.displayName || p.DisplayName || p.email || p.Email || 'Profesor'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Ciclo lectivo</label>
                  <input
                    type="number"
                    value={form.cicloLectivo}
                    onChange={(e) => handleChange('cicloLectivo', e.target.value)}
                    min="2000"
                    max="2100"
                  />
                </div>

                {message && (
                  <div className={`form-message ${messageType}`}>
                    <span>{message}</span>
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => navigate('/admin-dashboard', { state: { tab: 'aulas' } })}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      {isSubmitting && (
        <LoadingSpinner message="Guardando aula..." />
      )}
    </div>
  );
};

export default AdminEditAula;
